import styles from "./Range.module.css";
import React from "react";

export const Range = (props: {
  label: string;
  min: number;
  max: number;
  onChange: (label: string, value: number) => void;
  step?: number;
  value: number;
}) => {
  const [rect, setRect] = React.useState<DOMRect>();
  const ref = React.useRef<HTMLDivElement>();
  const handleRect = React.useCallback((node) => {
    ref.current = node;
    setRect(node?.getBoundingClientRect());
  }, []);

  const { label, max, min, onChange, step = 1, value } = props;
  const [isDragging, setIsDragging] = React.useState(false);

  const handleDragStart = React.useCallback((evt) => setIsDragging(true), []);
  const handleDragEnd = React.useCallback((evt) => setIsDragging(false), []);

  const handleChange = React.useCallback(
    (value: number) => {
      onChange(label, min + value);
    },
    [onChange, label, min]
  );

  const percent = React.useMemo(
    () => ((value - min) / (max - min)) * 100,
    [value, max, min]
  );

  React.useEffect(() => {
    const refreshRect = () => {
      if (ref.current) {
        setRect(ref.current.getBoundingClientRect());
      }
    };
    window.addEventListener("resize", refreshRect);

    return () => {
      window.removeEventListener("resize", refreshRect);
    };
  }, [ref]);

  React.useEffect(() => {
    if (!rect) {
      return;
    }

    const { left, right, width } = rect;

    let clientX = 0;
    let percent = 0;
    let value = 0;
    let rounded = 0;
    let final = 0;
    const handleDrag = (evt: any) => {
      if (isDragging) {
        clientX = evt.clientX;
        if (clientX > right) clientX = right;
        if (clientX < left) clientX = left;
        percent = (clientX - left) / width;
        value = percent * (max - min);
        rounded = Math.round(value / step);
        final = rounded * step;

        handleChange(final);
      }
    };

    window.addEventListener("mousemove", handleDrag);
    window.addEventListener("mouseup", handleDragEnd);

    return () => {
      window.removeEventListener("mousemove", handleDrag);
    };
  }, [isDragging, handleDragEnd, max, min, handleChange, step, rect]);

  return (
    <div
      className={styles.range}
      onMouseDown={handleDragStart}
      ref={handleRect}
    >
      <div
        className={styles.rangeValue}
        style={{
          width: `${percent}%`,
        }}
      ></div>
      <div className={styles.label}>
        {label}: {value.toFixed(2)}
      </div>
      <div className={styles.minMax}>
        <div>{min.toFixed(2)}</div>
        <div>{max.toFixed(2)}</div>
      </div>
    </div>
  );
};
